import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import classroomIconAsset from "../../assets/logos/classroom icon.png";
import mailIconAsset from "../../assets/logos/mail icon.png";
import oikosEduIconAsset from "../../assets/logos/Oikos_EDU_icon.png";
import pagesIconAsset from "../../assets/logos/Pages Icon.png";
import tablesIconAsset from "../../assets/logos/Tables icon.png";
import {
  PLATFORM_BRAND_KEYS,
  fetchPlatformBrandAssets,
  getPlatformBrandColor,
  getPlatformBrandIconOnly,
} from "../../platforms/admin/services/platformBrandingService";

const ICONS = {
  default: oikosEduIconAsset,
  defaultPng: oikosEduIconAsset,
  defaultApple: oikosEduIconAsset,
  edu: oikosEduIconAsset,
  workspace: "/oikos-edu-icon.svg",
  teacherBoard: "/app-icons/sports-falcon-icon.svg",
  campus: "/app-icons/campus-icon.svg",
  pages: pagesIconAsset,
  devices: "/app-icons/devices-icon-512.png",
  devicesApple: "/app-icons/devices-apple-touch-icon.png",
  profile: "/app-icons/profile-home-icon.png",
  tables: tablesIconAsset,
};

function upsertMeta(name, content) {
  let element = document.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function upsertLink(rel, href, attributes = {}) {
  let element = document.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function getIconType(iconPath) {
  const cleanPath = String(iconPath || "").split("?")[0].toLowerCase();
  if (cleanPath.endsWith(".png")) return "image/png";
  if (cleanPath.endsWith(".jpg") || cleanPath.endsWith(".jpeg")) return "image/jpeg";
  if (cleanPath.endsWith(".webp")) return "image/webp";
  if (cleanPath.endsWith(".ico")) return "image/x-icon";
  return "image/svg+xml";
}

function normalizeThemeColor(color = "", fallback = "#0f172a") {
  const clean = String(color || "").trim();
  if (/^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(clean)) return clean;
  return fallback;
}

function getInstallProfile(pathname, platformBrandAssets = {}) {
  const path = String(pathname || "").toLowerCase();
  const workspaceBrandIcon = getPlatformBrandIconOnly(platformBrandAssets, PLATFORM_BRAND_KEYS.workplace, ICONS.workspace);
  const workspaceBrandColor = normalizeThemeColor(getPlatformBrandColor(platformBrandAssets, PLATFORM_BRAND_KEYS.workplace, ""), "#868978");
  const teacherBoardBrandIcon = getPlatformBrandIconOnly(platformBrandAssets, PLATFORM_BRAND_KEYS.teacherBoard, ICONS.teacherBoard);
  const teacherBoardBrandColor = normalizeThemeColor(getPlatformBrandColor(platformBrandAssets, PLATFORM_BRAND_KEYS.teacherBoard, ""), "#2563EB");
  const mailBrandIcon = getPlatformBrandIconOnly(platformBrandAssets, PLATFORM_BRAND_KEYS.mail, mailIconAsset);
  const mailBrandColor = normalizeThemeColor(getPlatformBrandColor(platformBrandAssets, PLATFORM_BRAND_KEYS.mail, ""), "#B9A6FF");
  const pagesBrandIcon = getPlatformBrandIconOnly(platformBrandAssets, PLATFORM_BRAND_KEYS.pages, ICONS.pages);
  const pagesBrandColor = normalizeThemeColor(getPlatformBrandColor(platformBrandAssets, PLATFORM_BRAND_KEYS.pages, ""), "#4338ca");
  const tablesBrandIcon = getPlatformBrandIconOnly(platformBrandAssets, PLATFORM_BRAND_KEYS.tables, ICONS.tables);
  const tablesBrandColor = normalizeThemeColor(
    getPlatformBrandColor(platformBrandAssets, PLATFORM_BRAND_KEYS.workplace, "")
      || getPlatformBrandColor(platformBrandAssets, PLATFORM_BRAND_KEYS.tables, ""),
    "#15803d"
  );
  const classroomBrandIcon = getPlatformBrandIconOnly(platformBrandAssets, PLATFORM_BRAND_KEYS.classroom, classroomIconAsset);
  const classroomBrandColor = normalizeThemeColor(getPlatformBrandColor(platformBrandAssets, PLATFORM_BRAND_KEYS.classroom, ""), "#2F6352");

  if (path === "/login" || path.startsWith("/login/") || path === "/edu" || path.startsWith("/edu/")) {
    return {
      name: "Oikos EDU",
      shortName: "Oikos EDU",
      icon: ICONS.edu,
      appleIcon: ICONS.edu,
      themeColor: "#ff7a00",
      orientation: "any",
    };
  }

  if (pathname.startsWith("/devices") || pathname.startsWith("/studentdevice") || pathname.startsWith("/studentdevices")) {
    return {
      name: "Oikos Managed Devices",
      shortName: "Managed Devices",
      icon: ICONS.devices,
      appleIcon: ICONS.devicesApple,
      themeColor: "#d75c37",
      orientation: "landscape-primary",
    };
  }

  if (pathname.startsWith("/student")) {
    return {
      name: "Oikos Managed Devices",
      shortName: "Managed Devices",
      icon: ICONS.devices,
      appleIcon: ICONS.devicesApple,
      themeColor: "#d75c37",
      orientation: "landscape-primary",
    };
  }

  if (pathname.startsWith("/teacherboard")) {
    return {
      name: "Oikos TeacherBoard",
      shortName: "TeacherBoard",
      icon: teacherBoardBrandIcon,
      appleIcon: teacherBoardBrandIcon,
      themeColor: teacherBoardBrandColor,
      orientation: "any",
    };
  }

  if (pathname.startsWith("/campus") || pathname.startsWith("/teacher") || pathname.startsWith("/parent")) {
    return {
      name: "Oikos Campus",
      shortName: "Campus",
      icon: ICONS.campus,
      themeColor: "#E86A1F",
      orientation: "any",
    };
  }

  if (pathname.startsWith("/pages")) {
    return {
      name: "Oikos Pages",
      shortName: "Pages",
      icon: pagesBrandIcon,
      appleIcon: pagesBrandIcon,
      themeColor: pagesBrandColor,
      orientation: "any",
    };
  }

  if (pathname.startsWith("/workspace")) {
    return {
      name: "Oikos Workspace",
      shortName: "Workspace",
      icon: workspaceBrandIcon,
      appleIcon: workspaceBrandIcon,
      themeColor: workspaceBrandColor,
      orientation: "any",
    };
  }

  if (pathname.startsWith("/mail")) {
    return {
      name: "Oikos Mail",
      shortName: "Mail",
      icon: mailBrandIcon,
      appleIcon: mailBrandIcon,
      themeColor: mailBrandColor,
      orientation: "any",
    };
  }

  if (pathname.startsWith("/profile")) {
    return {
      name: "Oikos Profile",
      shortName: "Profile",
      icon: ICONS.profile,
      appleIcon: ICONS.profile,
      themeColor: "#2563eb",
      orientation: "any",
    };
  }

  if (pathname.startsWith("/tables")) {
    return {
      name: "Oikos Tables",
      shortName: "Tables",
      icon: tablesBrandIcon,
      appleIcon: tablesBrandIcon,
      themeColor: tablesBrandColor,
      orientation: "any",
    };
  }

  if (pathname.startsWith("/classroom")) {
    return {
      name: "Oikos Classroom",
      shortName: "Classroom",
      icon: classroomBrandIcon,
      appleIcon: classroomBrandIcon,
      themeColor: classroomBrandColor,
      orientation: "any",
    };
  }

  return {
    name: "Oikos EDU",
    shortName: "Oikos EDU",
    icon: ICONS.defaultPng,
    appleIcon: ICONS.defaultApple,
    themeColor: "#ff7a00",
    orientation: "any",
  };
}

export default function DynamicInstallMetadata() {
  const location = useLocation();
  const [platformBrandAssets, setPlatformBrandAssets] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetchPlatformBrandAssets().then((assets) => {
      if (!cancelled) setPlatformBrandAssets(assets || {});
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const startUrl = `${location.pathname}${location.search}${location.hash}`;
    const profile = getInstallProfile(location.pathname, platformBrandAssets);
    const manifest = {
      id: startUrl || "/",
      name: profile.name,
      short_name: profile.shortName,
      start_url: startUrl || "/",
      scope: "/",
      display: "standalone",
      orientation: profile.orientation,
      theme_color: profile.themeColor,
      background_color: profile.themeColor,
      icons: [
        {
          src: profile.icon,
          sizes: "any",
          type: getIconType(profile.icon),
          purpose: "any maskable",
        },
        ...(profile.appleIcon
          ? [
              {
                src: profile.appleIcon,
                sizes: "1024x1024",
                type: "image/png",
                purpose: "any maskable",
              },
            ]
          : []),
      ],
    };
    const manifestBlob = new Blob([JSON.stringify(manifest)], {
      type: "application/manifest+json",
    });
    const manifestUrl = URL.createObjectURL(manifestBlob);

    document.title = profile.name;
    upsertLink("manifest", manifestUrl);
    upsertLink("icon", profile.icon, { type: getIconType(profile.icon) });
    upsertLink("apple-touch-icon", profile.appleIcon || profile.icon);
    upsertMeta("theme-color", profile.themeColor);
    upsertMeta("apple-mobile-web-app-capable", "yes");
    upsertMeta("apple-mobile-web-app-title", profile.shortName);
    upsertMeta("apple-mobile-web-app-status-bar-style", "black-translucent");

    return () => {
      URL.revokeObjectURL(manifestUrl);
    };
  }, [location.hash, location.pathname, location.search, platformBrandAssets]);

  return null;
}
